import {
  createBrowserRouter,
  createMemoryRouter,
  createRoutesFromElements,
  Navigate,
  Route,
} from 'react-router-dom';

import { isDesktopApp } from 'utils/client-utils';
import { captureDesktopAppHint } from 'utils/desktop-app-hint';
import { setRouter } from 'utils/get-router';
import { exposeToGlobalConsole } from 'utils/react/expose-to-global-console';

import { lazyRoutes as ro } from './route-definitions';

const appRoutes = (
  <>
    <Route path="/" {...ro.AppShell}>
      <Route {...ro.DashboardShell}>
        <Route path="wait-for-browser-signin" {...ro.WaitForBrowserSignin} />
        <Route path="app-onboarding" {...ro.AppOnboarding} />
        <Route path="vo" key="vo">
          <Route {...ro.RequireAuth}>
            <Route {...ro.RequireAppOnboarding}>
              <Route {...ro.RequireOrg}>
                <Route index {...ro.DashboardHomePage} />
                <Route path="widget-settings">
                  <Route index {...ro.WidgetSettingsPage} />
                  <Route path=":orgId" {...ro.OrgWidgetSettingsPage} />
                </Route>
                <Route path="organizations" {...ro.OrganizationsPage} />
                <Route path=":orgId">
                  <Route index {...ro.OrgDetailsPage} />
                  <Route path="members">
                    <Route index {...ro.OrgMembersPage} />
                    <Route path="invite" {...ro.DashboardOrgInvitePage} />
                  </Route>
                  <Route path="org-teams" {...ro.OrgTeamsPage} />
                  <Route path="org-settings" {...ro.OrgSettingsPage} />
                  <Route path="org-leave" {...ro.DashboardLeaveOrgConfirmationPage} />
                  <Route path="teams/*">
                    <Route index element={<Navigate to="../org-teams" replace />} />
                    <Route path=":teamId">
                      <Route path="edit" {...ro.DashboardAddOrEditTeamPage} />
                      <Route path="delete" {...ro.DashboardDeleteTeamConfirmationPage} />
                    </Route>
                    <Route path="new" {...ro.DashboardAddOrEditTeamPage} />
                    <Route path="all" {...ro.AllTeams} />
                  </Route>
                  <Route path="rooms">
                    <Route index element={<Navigate to=".." replace />} />
                    <Route path="new" {...ro.SpaceSettings} />
                    <Route path="all" {...ro.AllSpaces} />
                    <Route path=":spaceId">
                      <Route path="settings" {...ro.SpaceSettings} />
                    </Route>
                  </Route>
                  <Route path="instant-meetings">
                    <Route index element={<Navigate to="new" />} />
                    <Route path="new" {...ro.NewMeeting} />
                    <Route path="join" {...ro.JoinMeeting} />
                    <Route path=":instantMeetingId">
                      <Route index element={<Navigate to=".." replace />} />
                    </Route>
                  </Route>
                  <Route path="people/*">
                    <Route path=":userId" {...ro.Person} />
                    <Route path="settings" {...ro.PeopleSettings} />
                    <Route index {...ro.AllPeople} />
                  </Route>
                </Route>
                <Route path=":orgId/schedule-meeting">
                  <Route index {...ro.ScheduleMeeting} />
                </Route>
              </Route>

              <Route path="create-or-join-org" {...ro.CreateOrJoinOrgPage} />
              <Route path="new-org">
                <Route index {...ro.DashboardNewOrgPage} />
                <Route path="manual">
                  <Route index {...ro.DashboardNewManualOrgPage} />
                  <Route path="success/:orgId" {...ro.DashboardManualOrgCreatedPage} />
                </Route>
                <Route path="slack" {...ro.DashboardNewSlackOrgPage} />
              </Route>
            </Route>
          </Route>
        </Route>
      </Route>
    </Route>
    {import.meta.env.MODE !== 'production' && (
      <Route path="dev">
        {/* Ballpark development routes - gives access to test apps and allows bot clients to bypass auth */}
        <Route path="ballpark/netcode-test" {...ro.BallparkNetcodeTest} />
        {/* Pile component visualizer for testing avatar pile layouts */}
        <Route path="pile-visualizer" {...ro.PileVisualizer} />
        {/* Pop design lab — renders the REAL ui-kit / HUD components with mock props. See HOW_TO_USE.md */}
        <Route path="pop-design-lab-react" {...ro.PopDesignLabReact} />
      </Route>
    )}
  </>
);

const createDesktopRouter = () => {
  const desktopRouter = createMemoryRouter(createRoutesFromElements(<>{appRoutes}</>));
  exposeToGlobalConsole({ desktopRouter });
  void desktopRouter.navigate('/vo');
  return desktopRouter;
};

const createWebAppRouter = () => {
  const webAppRouter = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route {...ro.WebShell}>
          <Route path="/" {...ro.Home} />
          <Route path="beta" element={<Navigate to="/" replace />} />
          <Route path="signup" {...ro.SignUp} />
          <Route path="signin/:token" {...ro.SignIn} />
          <Route path="signin" {...ro.SignIn} />
          <Route path="signout" {...ro.SignOut} />
          <Route {...ro.RequireAuth}>
            <Route path="open-desktop-app/:loginToken?" {...ro.OpenDesktopApp} />
            <Route path="link-with-slack" {...ro.LinkWithSlack} />
          </Route>
          <Route path="login" element={<Navigate to="/signin" />} />
          <Route path="logout" element={<Navigate to="/signout" />} />
          <Route path="verify-email" {...ro.VerifyEmail} />
          <Route path="verify-email/:email" {...ro.VerifyEmail} />
          <Route path="link-account/:email/code/:code" {...ro.LinkAccount} />
          <Route
            path="google-login-redirect/:customToken/redirectToApp/:redirectToApp"
            {...ro.GoogleLoginRedirect}
          />
          <Route path="google-login-redirect/:error" {...ro.GoogleLoginRedirect} />
          {/* Bare path: the page strips the code from the URL while redeeming, so a reload or
              Back lands here — the component routes it to /signin instead of a router error. */}
          <Route path="okta-login-redirect" {...ro.OktaLoginRedirect} />
          <Route path="okta-login-redirect/error" {...ro.OktaLoginRedirect} />
          <Route path="okta-login-redirect/:code" {...ro.OktaLoginRedirect} />
          <Route path="okta-link-account/:ticket" {...ro.OktaLinkAccount} />
          <Route path="reset-password" {...ro.ResetPassword} />
          <Route path="reset-password/:encodedEmail" {...ro.ResetPassword} />
          <Route path="reset-password/:encodedEmail/code/:code" {...ro.ResetPassword} />
          <Route path="download" {...ro.DownloadApp} />
          <Route path="download/:platform" {...ro.DownloadAppPlatform} />
          <Route {...ro.RequireAuth}>
            <Route path="/pricing" {...ro.Pricing} />
          </Route>
          {/* Retain the flat illustration for comparison. */}
          <Route path="home" {...ro.HomePreview} />
          {/* Dev-only alias for preview tooling. */}
          {import.meta.env.MODE !== 'production' && <Route path="dev/home-preview" {...ro.HomePreview} />}
          <Route path="3D-hero" element={<Navigate to="/" replace />} />
          <Route path="u" key="urls">
            <Route path="org-invite/:urlId" {...ro.AcceptOrgInvite} />
          </Route>
        </Route>

        {appRoutes}
      </>,
    ),
  );
  return webAppRouter;
};

const desktopRouter = createDesktopRouter();
export const webRouter = createWebAppRouter();
export const router = isDesktopApp ? desktopRouter : webRouter;
// Browser sign-in for a Pop-preview desktop build: remember `?app=pop-preview` across the sign-in round-trip.
if (!isDesktopApp) captureDesktopAppHint(window.location);
setRouter(router);
